'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';

/**
 * Props for `CalloutAction`.
 */
export type CalloutActionProps =
  SliceComponentProps<Content.CalloutActionSlice>;

/**
 * Component for "CalloutAction" Slices.
 *
 * Features:
 * - Icon at the top (e.g., alert icon)
 * - Headline
 * - Description text
 * - Supporting text in a card with gradient background
 * - Primary CTA button
 */
const CalloutAction: FC<CalloutActionProps> = ({ slice }) => {
  const {
    icon,
    headline,
    description,
    supporting_text,
    under_support_description,
    primary_cta,
  } = slice.primary;

  const iconRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          entry.target.classList.remove('opacity-0', 'translate-y-8');
        }
      });
    }, observerOptions);

    const elementsToAnimate = [
      { ref: iconRef, delay: '0ms' },
      { ref: contentRef, delay: '150ms' },
      { ref: cardRef, delay: '300ms' },
    ];

    elementsToAnimate.forEach(({ ref, delay }) => {
      if (ref.current) {
        ref.current.classList.add(
          'opacity-0',
          'translate-y-8',
          'transition-all',
          'duration-700',
          'ease-out',
        );
        ref.current.style.transitionDelay = delay;
        observer.observe(ref.current);
      }
    });

    return () => {
      elementsToAnimate.forEach(({ ref }) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative py-16 md:py-20 lg:py-28 px-4 md:px-8 lg:px-[150px]"
      style={{
        background:
          'linear-gradient(180deg, #0E1110 0%, #221618 25.96%, #221618 73.56%, #0E1110 100%)',
      }}
    >
      <div className="max-w-[1040px] mx-auto flex flex-col gap-[60px] items-center">
        <div className="flex flex-col gap-6 items-center w-full">
          {/* Icon */}
          {isFilled.image(icon) && (
            <div ref={iconRef} className="w-[70px] h-[70px] flex-shrink-0">
              <PrismicNextImage
                field={icon}
                className="w-full h-full object-contain"
              />
            </div>
          )}

          {/* Content: Headline + Description */}
          <div
            ref={contentRef}
            className="flex flex-col gap-6 items-center text-center"
          >
            {/* Headline */}
            {isFilled.richText(headline) && (
              <div className="text-[28px] md:text-[40px] font-semibold leading-[1.2] text-white max-w-[806px]">
                <PrismicRichText
                  field={headline}
                  components={{
                    heading2: ({ children }) => <h2>{children}</h2>,
                  }}
                />
              </div>
            )}

            {/* Description */}
            {isFilled.richText(description) && (
              <div className="text-base md:text-lg font-normal leading-[1.5] text-white max-w-[746px]">
                <PrismicRichText field={description} />
              </div>
            )}
          </div>

          {/* Card with Supporting Text and CTA */}
          <div
            ref={cardRef}
            className="w-full max-w-[800px] bg-gradient-to-b from-[rgba(0,0,0,0.4)] to-transparent rounded-3xl p-6 flex flex-col gap-6 items-center"
          >
            {/* Supporting Text (Bold Headline) */}
            {isFilled.richText(supporting_text) && (
              <div className="text-[24px] md:text-[32px] font-bold leading-[1.3] text-white text-center max-w-[750px]">
                <PrismicRichText
                  field={supporting_text}
                  components={{
                    heading3: ({ children }) => <h3>{children}</h3>,
                    paragraph: ({ children }) => <p>{children}</p>,
                  }}
                />
              </div>
            )}

            {/* Under Support Description (Regular text) */}
            {isFilled.keyText(under_support_description) && (
              <p className="text-base md:text-lg font-normal leading-[1.5] text-white text-center">
                {under_support_description}
              </p>
            )}

            {/* Primary CTA Button - Scrolls to form */}
            <button
              type="button"
              onClick={() => {
                const formSection = document.querySelector(
                  '[data-slice-type="cta_with_side_form"]',
                );
                if (formSection) {
                  formSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="bg-[rgba(234,234,234,0.3)] border-t border-[#eaeaea] rounded-full px-6 py-3 font-bold text-white text-base text-center hover:bg-white/40 transition-colors cursor-pointer"
            >
              {isFilled.link(primary_cta) && primary_cta.text
                ? primary_cta.text
                : 'Get My Free Checklist'}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CalloutAction;
